import _ from 'lodash';
import { observer } from 'mobx-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerInfo } from 'hooks/useCustomerInfo';
import APP from 'stores/APP';
import security from 'stores/security';
import { OpenPermStatusEnum } from 'types/customer';
import classlist from 'utils/classlist';
import { DashboardItemConfigType, getBlocksMap, getStatusMap, StatusMapItemType } from './config';
import './style.scss';

const Dashboard: React.FC = observer(() => {
    const blocksMap = getBlocksMap();
    const { currentProcessStatus, currentAccountType, isClosingOrDeleting } = security;
    // FD账户不支持账单报告
    const curProcessStatus =
        currentAccountType === 'ib' && currentProcessStatus === 'FUNDED' ? 'FDFUNDED' : currentProcessStatus;
    const pageBlocks: StatusMapItemType = getStatusMap()[curProcessStatus].filter(item => !blocksMap[item[0]]?.hidden);
    const navigate = useNavigate();
    const { data: customerInfo } = useCustomerInfo();
    let tempPageBlocks = pageBlocks;

    // 关户中、注销中或者ib的用户“出金”，“入金”，“转户”模块无效
    if (isClosingOrDeleting || !security.isMarscoAccount) {
        tempPageBlocks = tempPageBlocks?.map(([block, valid]) => [
            block,
            ['withdraw', 'deposit', 'transfer', 'report'].includes(block) ? false : valid
        ]);
    }

    const handleBlockClick = async (block: DashboardItemConfigType) => {
        if (block.eventName) {
            APP.trackEvent('首页导航', '导航', block.eventName);
        }

        // 默认的跳转逻辑
        function redirect() {
            if (block.link) {
                if (block.external) {
                    // 针对外部链接
                    APP.updateHref(block.link);
                } else {
                    navigate(`/profile${block.link}`);
                }
            }
        }

        switch (block.key) {
            // 如果是立即开户，也需要判断是否是账户升级（另外一个户只要开了就是账户升级）
            case 'open':
                // setTimeout是为了解决神策埋点统计不上的hack
                // 一人多户下, 理论上不会存在某个账户已经有状态, 然后切换到一个未开户的账户
                // 所以此处不需要处理跳向升级账户(开第二个户)的情况
                setTimeout(() => {
                    redirect();
                }, 300);

                break;

            case 'resubmit':
                /**
                 * 处理账户打回状态
                 * 资料打回直接跳转modify
                 */
                APP.updateHref(`/accounts/modify/${security.currentCustomerId}`);
                break;

            case 'continue':
                APP.updateHref(`/accounts/open/${security.currentCustomerId}`);
                break;

            case 'syep':
                /**
                 * 如果没有syep权限, 就跳转去开通syep权限
                 * 如果已经有了syep权限, 就去展示syep的收益
                 */
                if (customerInfo?.enhance_perm === OpenPermStatusEnum.NOT_OPENED) {
                    // syep/后面的/必须要带, h5-repos-us对应的地址就是这个
                    APP.updateHref('/activity/forapp/syep/');
                } else {
                    redirect();
                }

                break;

            default:
                redirect();
        }
    };

    const contents: React.ReactElement[] = tempPageBlocks?.map(item => {
        const block = blocksMap[item[0]];

        return (
            <div
                className={classlist('dashboard-item', !item[1] && 'dashboard-item-disabled')}
                key={block.key}
                onClick={() => item[1] && handleBlockClick(block)}>
                <span className={classlist('item-icon', block.className)} />
                <div className="item-content">
                    <h2>
                        <span className="item-content-title">{block.title}</span>
                    </h2>
                    <p>{block.desc}</p>
                </div>
                {block.warning && <span className="item-warning">{block.warning}</span>}
            </div>
        );
    });

    return (
        <div className="dashboard-page">
            <div className="dashboard-list">{contents}</div>
        </div>
    );
});

export default Dashboard;
